import { authFetch, handleSessionExpired } from './authService';

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:8001'}/api/v1`;

export interface LockInfo {
  locked_by_id: string;
  lock_expires_at: string;
}

/** PROD1.4.6 hotfix : retourné par POST /lock si doc sans extraction courante. */
export interface NoExtractionResult {
  no_extraction: true;
}

export type AcquireLockResult = LockInfo | NoExtractionResult;

export function isNoExtraction(r: AcquireLockResult): r is NoExtractionResult {
  return (r as NoExtractionResult).no_extraction === true;
}

export interface LockConflict {
  code: 'extraction_locked';
  locked_by: { id: string | null; email: string | null };
  lock_expires_at: string | null;
}

export class LockConflictError extends Error {
  conflict: LockConflict;
  constructor(conflict: LockConflict) {
    super('extraction_locked');
    this.name = 'LockConflictError';
    this.conflict = conflict;
  }
}

export class NotLockHolderError extends Error {
  constructor() {
    super('not_lock_holder');
    this.name = 'NotLockHolderError';
  }
}

function handleAuthFailure(response: Response) {
  if (response.status === 401) {
    handleSessionExpired(response);
  }
}

export async function acquireLock(documentId: string): Promise<AcquireLockResult> {
  const response = await authFetch(`${API_BASE}/documents/${documentId}/lock`, {
    method: 'POST',
  });

  handleAuthFailure(response);

  if (response.status === 200) {
    return response.json();
  }

  if (response.status === 409) {
    const body = await response.json().catch(() => null);
    const detail = body?.detail ?? body;
    if (detail && detail.code === 'extraction_locked') {
      throw new LockConflictError(detail as LockConflict);
    }
    throw new Error('extraction_locked');
  }

  const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
  throw new Error(error.detail || `Erreur ${response.status}`);
}

export async function releaseLock(documentId: string): Promise<void> {
  const response = await authFetch(`${API_BASE}/documents/${documentId}/lock`, {
    method: 'DELETE',
  });

  handleAuthFailure(response);

  // 403 (not holder) and 404 (lock already gone / no current extraction) are
  // expected on cleanup — swallow silently.
  if (response.status === 403 || response.status === 404) {
    return;
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
    throw new Error(error.detail || `Erreur ${response.status}`);
  }
}

export async function extendLock(documentId: string): Promise<{ lock_expires_at: string }> {
  const response = await authFetch(`${API_BASE}/documents/${documentId}/lock/extend`, {
    method: 'POST',
  });

  handleAuthFailure(response);

  if (response.status === 403) {
    throw new NotLockHolderError();
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
    throw new Error(error.detail || `Erreur ${response.status}`);
  }

  return response.json();
}
